/**
 * Box Prompt Prediction Cache Slice
 *
 * Caches predictions from box prompting inference to enable progressive reveal
 * in "Find Similar Objects" without re-running inference.
 *
 * When box prompting inference runs (either from auto-generate or "Find Similar Objects"),
 * ALL predictions are cached sorted by confidence. Subsequent clicks on
 * "Find Similar Objects" reveal the next prediction from the cache (instant, no network call).
 */

import type { Prediction } from "@/utils/predictionFiltering";
import { RapidStateCreator } from "../types";

/**
 * Cache entry for a specific file/frame/label combination.
 */
export interface BoxPromptCacheEntry {
    /** All predictions from box prompting, sorted by confidence descending */
    predictions: Prediction[];
    /** Index of the next prediction to reveal (0-based) */
    nextToRevealIndex: number;
    /** Timestamp when the cache was created (for staleness checks) */
    createdAt: number;
}

/**
 * Cache key format: `${fileId}:${frameId}:${label}`
 */
type CacheKey = string;

export interface BoxPromptCacheSliceState {
    /** Cache of box prompt predictions by file/frame/label */
    boxPromptCache: Map<CacheKey, BoxPromptCacheEntry>;

    /**
     * Sets cached predictions for a specific file/frame/label.
     * Called after box prompting inference completes.
     */
    setBoxPromptCache: (
        fileId: string,
        frameId: string,
        label: string,
        predictions: Prediction[],
        nextToRevealIndex: number
    ) => void;

    /**
     * Gets cached predictions for a specific file/frame/label.
     * Returns null if no cache exists.
     */
    getBoxPromptCache: (
        fileId: string,
        frameId: string,
        label: string
    ) => BoxPromptCacheEntry | null;

    /**
     * Increments the nextToRevealIndex for a specific file/frame/label.
     * Called when user clicks "Find Similar Objects" to reveal the next prediction.
     */
    incrementBoxPromptCacheIndex: (fileId: string, frameId: string, label: string) => void;

    /**
     * Clears cache for a specific file/frame/label.
     */
    clearBoxPromptCacheForLabel: (fileId: string, frameId: string, label: string) => void;

    /**
     * Clears all cache for a specific file/frame (all labels).
     */
    clearBoxPromptCacheForFrame: (fileId: string, frameId: string) => void;

    /**
     * Clears all cache for a specific file (all frames and labels).
     */
    clearBoxPromptCacheForFile: (fileId: string) => void;

    /**
     * Clears all box prompt cache.
     */
    clearAllBoxPromptCache: () => void;
}

function buildCacheKey(fileId: string, frameId: string, label: string): CacheKey {
    return `${fileId}:${frameId}:${label}`;
}

export const createBoxPromptCacheSlice: RapidStateCreator<BoxPromptCacheSliceState> = (
    set,
    get
) => ({
    boxPromptCache: new Map(),

    setBoxPromptCache: (fileId, frameId, label, predictions, nextToRevealIndex) => {
        const key = buildCacheKey(fileId, frameId, label);
        set((state) => {
            const newCache = new Map(state.boxPromptCache);
            newCache.set(key, { predictions, nextToRevealIndex, createdAt: Date.now() });
            return { boxPromptCache: newCache };
        });
    },

    getBoxPromptCache: (fileId, frameId, label) => {
        const key = buildCacheKey(fileId, frameId, label);
        return get().boxPromptCache.get(key) ?? null;
    },

    incrementBoxPromptCacheIndex: (fileId, frameId, label) => {
        const key = buildCacheKey(fileId, frameId, label);
        set((state) => {
            const entry = state.boxPromptCache.get(key);
            if (!entry) return state;

            const newCache = new Map(state.boxPromptCache);
            newCache.set(key, { ...entry, nextToRevealIndex: entry.nextToRevealIndex + 1 });
            return { boxPromptCache: newCache };
        });
    },

    clearBoxPromptCacheForLabel: (fileId, frameId, label) => {
        const key = buildCacheKey(fileId, frameId, label);
        set((state) => {
            const newCache = new Map(state.boxPromptCache);
            newCache.delete(key);
            return { boxPromptCache: newCache };
        });
    },

    clearBoxPromptCacheForFrame: (fileId, frameId) => {
        const prefix = `${fileId}:${frameId}:`;
        set((state) => {
            const newCache = new Map(state.boxPromptCache);
            for (const key of newCache.keys()) {
                if (key.startsWith(prefix)) {
                    newCache.delete(key);
                }
            }
            return { boxPromptCache: newCache };
        });
    },

    clearBoxPromptCacheForFile: (fileId) => {
        const prefix = `${fileId}:`;
        set((state) => {
            const newCache = new Map(state.boxPromptCache);
            for (const key of newCache.keys()) {
                if (key.startsWith(prefix)) {
                    newCache.delete(key);
                }
            }
            return { boxPromptCache: newCache };
        });
    },

    clearAllBoxPromptCache: () => {
        set({ boxPromptCache: new Map() });
    }
});
